// Px3 Pipeline - queue depth.
//
// Two queues matter to a build. `px3` is ours alone: the reconciler tick and
// every unit job. The `panel` queue is SHARED - domain_add and the MasterInbox
// trust run ride it alongside Sequencer Connect and OAuth Trust runs, at two
// browser slots per droplet - so a px3 order can be blocked by work that has
// nothing to do with it. That is why both are on one screen: "nothing is
// happening" and "something else has the browsers" look identical from the
// order detail, and only this view tells them apart.

import { useCallback, useEffect, useState } from 'react';

import { px3Api, px3ErrorMessage, type Px3QueueStats } from '../px3Api';

/** Same 10s as the other list screens. */
const POLL_MS = 10_000;

/** BullMQ's own count keys, in the order they tell a story: what is waiting,
 *  what is running, what is backing off, what gave up. */
const PX3_KEYS: { key: string; label: string; variant: string; hint: string }[] = [
  { key: 'waiting', label: 'Waiting', variant: 'blue', hint: 'queued, no worker yet' },
  { key: 'active', label: 'Active', variant: 'blue', hint: 'a worker has it now' },
  { key: 'delayed', label: 'Delayed', variant: 'honey', hint: 'backing off or scheduled' },
  { key: 'failed', label: 'Failed', variant: 'rose', hint: 'out of attempts' },
  { key: 'completed', label: 'Completed', variant: 'muted', hint: 'kept briefly, then trimmed' },
  { key: 'paused', label: 'Paused', variant: 'muted', hint: 'queue-level pause' },
];

/** The job-name prefixes the router counts on the shared panel queue. */
const PANEL_KEYS: { key: string; label: string; hint: string }[] = [
  { key: 'px3_panel', label: 'px3 panel', hint: 'domain_add browser jobs' },
  { key: 'px3_mi', label: 'px3 MasterInbox', hint: 'MasterInbox connect jobs' },
  { key: 'sequencer_connect', label: 'Sequencer Connect', hint: 'another feature, same slots' },
  { key: 'oauth_trust', label: 'OAuth Trust', hint: 'another feature, same slots' },
];

function Tile({
  label,
  value,
  variant,
  hint,
}: {
  label: string;
  value: number;
  variant: string;
  hint: string;
}): JSX.Element {
  // A zero is not news; grey it so the eye lands on the counts that are.
  const paint = value === 0 ? 'muted' : variant;
  return (
    <div className="px3-tile">
      <span className="px3-tile-label">{label}</span>
      <span className={`px3-tile-value px3-tile-value--${paint}`}>{value}</span>
      <span className="px3-tile-hint">{hint}</span>
    </div>
  );
}

export function QueueView(): JSX.Element {
  const [stats, setStats] = useState<Px3QueueStats | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async (): Promise<void> => {
    try {
      setStats(await px3Api.getQueue());
      setError(null);
    } catch (err) {
      setError(px3ErrorMessage(err));
    }
  }, []);

  useEffect(() => {
    void refresh();
    const timer = window.setInterval(() => void refresh(), POLL_MS);
    return () => window.clearInterval(timer);
  }, [refresh]);

  return (
    <>
      {error && (
        <div className="px3-toast px3-toast--error">
          Could not read the queues: {error}. Redis or the px3 queue may be down - the counts below,
          if any, are the last good read.
        </div>
      )}

      <section className="px3-card">
        <div className="px3-card-header">
          <h2 className="px3-card-title">px3 queue</h2>
          <div className="px3-actions">
            <button
              type="button"
              className="px3-btn px3-btn--ghost px3-btn--small"
              onClick={() => void refresh()}
            >
              Refresh
            </button>
          </div>
        </div>
        <div className="px3-card-body">
          {stats === null ? (
            <div className="px3-empty">{error === null ? 'Loading queue counts...' : 'No counts.'}</div>
          ) : (
            <div className="px3-tiles">
              {PX3_KEYS.map((k) => (
                <Tile
                  key={k.key}
                  label={k.label}
                  value={stats.px3[k.key] ?? 0}
                  variant={k.variant}
                  hint={k.hint}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="px3-card">
        <div className="px3-card-header">
          <h2 className="px3-card-title">panel queue (shared)</h2>
          <span className="px3-hint">
            Waiting, active and delayed jobs by name prefix. Two browser slots per droplet serve all
            of these, so a queue of Sequencer Connect work delays px3 domain_add.
          </span>
        </div>
        <div className="px3-card-body">
          {stats === null ? (
            <div className="px3-empty">{error === null ? 'Loading queue counts...' : 'No counts.'}</div>
          ) : (
            <div className="px3-tiles">
              {PANEL_KEYS.map((k) => (
                <Tile
                  key={k.key}
                  label={k.label}
                  value={stats.panel[k.key] ?? 0}
                  variant={k.key.startsWith('px3') ? 'blue' : 'honey'}
                  hint={k.hint}
                />
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  );
}
